import { useId, useState } from 'react';
import { ApiError } from '../../identity/types';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { peopleGateway } from '../index';
import { emptyContractorDraft, toCreateContractorInput, validateContractorDraft, type ContractorDraft, type ContractorDraftErrors } from '../contractorForm';
import { ContractorPayMethod, contractorIdentificationTypeLabels, contractorPayMethodLabels, type ContractorResponse } from '../types';

export interface AddContractorDialogProps {
  onClose: () => void;
  /** Called with the saved contractor; the caller selects it and returns focus. */
  onCreated: (contractor: ContractorResponse) => void;
  /** Pay Contractors opens this with Check pre-selected, because it only pays by check. */
  defaultPayMethod?: number | null;
  /** Extra class for the dialog skin (Contractor Payments uses its sheet-style scrolling dialog). */
  className?: string;
}

/**
 * EPN-250 — the "Add contractor" prompt that opens OVER Pay Contractors so a missing contractor can be created without leaving the page (product
 * principle: a dependency can be added in place). It uses the same create API and the same form mapping (`contractorForm.ts`) as the Contractors list.
 * The page underneath stays mounted, so nothing already entered is lost. The server decides permission (`contractors.write`), tenant scoping and
 * validation; a refusal is shown here in plain language.
 */
export function AddContractorDialog({ onClose, onCreated, defaultPayMethod = ContractorPayMethod.Check, className }: AddContractorDialogProps) {
  const uid = useId();
  const [draft, setDraft] = useState<ContractorDraft>({ ...emptyContractorDraft, preferredPayMethod: defaultPayMethod === null ? '' : String(defaultPayMethod) });
  const [errors, setErrors] = useState<ContractorDraftErrors>({});
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof ContractorDraft>(key: K, value: ContractorDraft[K]) => setDraft((d) => ({ ...d, [key]: value }));

  async function submit() {
    const found = validateContractorDraft(draft);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setBusy(true);
    setError(null);
    try {
      onCreated(await peopleGateway.createContractor(toCreateContractorInput(draft)));
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true);
        setError('You don’t have permission to add contractors. Ask an administrator to add this contractor, then choose it from the list.');
      } else {
        setError(friendlyErrorMessage(err, 'The contractor couldn’t be added. Try again.'));
      }
      setBusy(false);
    }
  }

  return (
    <ModalDialog
      title="Add contractor"
      size="md"
      {...(className ? { className } : {})}
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          {!forbidden && <button type="button" className="jr-btn jr-btn--primary" onClick={() => void submit()} disabled={busy}>{busy ? 'Adding…' : 'Add contractor'}</button>}
        </>
      )}
    >
      <p className="muted">The contractor is saved for your company, like on the Contractors page. Everything you have entered on the payment is kept.</p>
      <div className="jr-field">
        <label htmlFor={`${uid}-name`}>Display name <span aria-hidden="true" className="jr-req">*</span></label>
        <input id={`${uid}-name`} data-autofocus value={draft.displayName} aria-required="true" aria-invalid={errors.displayName ? true : undefined} onChange={(e) => set('displayName', e.target.value)} />
        {errors.displayName && <p className="jr-error" role="alert">{errors.displayName}</p>}
      </div>
      <div className="jr-field">
        <label htmlFor={`${uid}-business`}>Business name</label>
        <input id={`${uid}-business`} value={draft.businessName} placeholder="(individual)" onChange={(e) => set('businessName', e.target.value)} />
      </div>
      <div className="jr-field">
        <label htmlFor={`${uid}-email`}>Email address <span aria-hidden="true" className="jr-req">*</span></label>
        <input id={`${uid}-email`} type="email" value={draft.email} aria-required="true" aria-invalid={errors.email ? true : undefined} onChange={(e) => set('email', e.target.value)} />
        {errors.email && <p className="jr-error" role="alert">{errors.email}</p>}
      </div>
      <div className="jr-field">
        <label htmlFor={`${uid}-idtype`}>Identification type</label>
        <select id={`${uid}-idtype`} value={draft.identificationType} onChange={(e) => set('identificationType', Number(e.target.value))}>
          {Object.entries(contractorIdentificationTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>
      <div className="jr-field">
        <label htmlFor={`${uid}-last4`}>Identification number (last 4)</label>
        <input id={`${uid}-last4`} value={draft.identificationLast4} maxLength={4} inputMode="numeric" placeholder="1234" aria-invalid={errors.identificationLast4 ? true : undefined} onChange={(e) => set('identificationLast4', e.target.value)} />
        {errors.identificationLast4 && <p className="jr-error" role="alert">{errors.identificationLast4}</p>}
      </div>
      <div className="jr-field">
        <label htmlFor={`${uid}-method`}>Preferred pay method</label>
        <select id={`${uid}-method`} value={draft.preferredPayMethod} onChange={(e) => set('preferredPayMethod', e.target.value)}>
          <option value="">Not specified</option>
          {Object.entries(contractorPayMethodLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>
      <div className="jr-field">
        <label htmlFor={`${uid}-w9`}>W-9 received on</label>
        <input id={`${uid}-w9`} type="date" value={draft.w9ReceivedOn} onChange={(e) => set('w9ReceivedOn', e.target.value)} />
      </div>
      <label className="jr-field">
        <span><input type="checkbox" checked={draft.form1099Configured} onChange={(e) => set('form1099Configured', e.target.checked)} /> 1099 reporting is set up for this contractor</span>
      </label>
      {error && <p className="jr-error" role="alert">{error}</p>}
    </ModalDialog>
  );
}
